import { Brand } from "types";
import { getBrandedUrl } from "utils/getBrandedUrl";

export function getBrandCode(brandSite: string | null): Brand | undefined {
  const BRAND_SITE_MAP: Record<string, Brand> = {
    rh: "RH",
    "baby & child": "BC",
    baby: "BC",
    teen: "TN",
    // no subdomain exists for water works.
    waterworks: "RH"
  };

  //if we have brand site parameter, look up the brand code
  if (brandSite) {
    const normalizedBrandSite = brandSite.trim().toLowerCase();
    return BRAND_SITE_MAP[normalizedBrandSite];
  }
}

function buildPreservedQuery(params: URLSearchParams): string {
  const preservedParams = new URLSearchParams();
  ["env_id", "inline_preview"].forEach(name => {
    const value = params.get(name);
    if (value) preservedParams.set(name, value);
  });
  return preservedParams.toString();
}

type RedirectResult = { url: string; external: boolean } | { message: string };

function computePathPart(
  contentType: string,
  categoryType: string | null,
  id: string | undefined
): string | null {
  // Handle globalNavigation separately as it does not require an id
  if (contentType === "globalNavigation") {
    return `/`;
  }

  // For other content types, id is required
  if (!id) return null;

  const staticPaths: Record<string, string> = {
    product: `/catalog/product/product.jsp/${id}`,
    promotionDisplay: `/content/promo.jsp?id=${id}`
  };

  if (contentType === "category") {
    const normalizedCategoryType = (
      categoryType || "Product Gallery"
    ).toLowerCase();

    const categoryPaths: Record<string, string> = {
      "collection gallery": `/catalog/category/collections.jsp/${id}`,
      "product gallery": `/catalog/category/products.jsp/${id}`
    };
    return categoryPaths[normalizedCategoryType] || null;
  }

  return staticPaths[contentType] || null;
}

function getPreviewRedirectUrl(query: string, id?: string): RedirectResult {
  const params = new URLSearchParams(query);
  const locale = params.get("locale") || "en-US";
  const contentType = params.get("contentType");

  if (!contentType) {
    return { message: "Missing required parameters." };
  }

  const [lang, country] = locale.split("-");
  if (!lang || !country) {
    return { message: "Invalid locale format." };
  }

  const categoryType = params.get("categoryType");
  const brandParam = params.get("brandSite") || params.get("brand");
  const brand = getBrandCode(brandParam);

  const pathPart = computePathPart(contentType, categoryType, id);
  if (!pathPart) {
    return { message: `Unsupported content type: ${contentType}` };
  }

  const basePath = `/${country.toLowerCase()}/${lang}${pathPart}`;
  const redirectPath = brand ? getBrandedUrl(basePath, brand) : basePath;

  const preservedQuery = buildPreservedQuery(params);
  const fullUrl = preservedQuery
    ? `${redirectPath}?${preservedQuery}`
    : redirectPath;

  const isExternal = /^https?:\/\//i.test(redirectPath);
  return { url: fullUrl, external: isExternal };
}

export { getPreviewRedirectUrl };
